import React, { useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Button,
	DatePicker,
	Descriptions,
	Drawer,
	Image,
	Input,
	Modal,
	Pagination,
	Select,
	Space,
	Spin,
	Table,
	Tabs,
	Tag,
	message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import { configApp } from '../../config/config';
import {
	AdminTimesheet,
	getAdminTimesheetDetail,
	getAdminTimesheetGroups,
	getAdminTimesheetMembers,
	getAdminTimesheets,
	restoreAdminTimesheets,
	TimesheetGroupOption,
	TimesheetMemberOption,
	TimesheetStatusFilter,
} from '../../service/timesheet/TimesheetService';

const { RangePicker } = DatePicker;

const createRequestId = () => {
	if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
	return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatMinute = (value?: number) => {
	const minutes = Number(value);
	if (!Number.isFinite(minutes) || minutes <= 0) return '-';
	return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.round(minutes % 60)).padStart(2, '0')}`;
};

const memberName = (row?: AdminTimesheet['member']) =>
	[row?.fname, row?.lname].filter(Boolean).join(' ').trim() || row?.email || '-';

export default function Timesheets() {
	const [status, setStatus] = useState<TimesheetStatusFilter>('deleted');
	const [dateRange, setDateRange] = useState<[Moment, Moment]>([
		moment().subtract(29, 'days').startOf('day'),
		moment().endOf('day'),
	]);
	const [groupId, setGroupId] = useState<string>();
	const [groupUserId, setGroupUserId] = useState<string>();
	const [search, setSearch] = useState('');
	const [appliedSearch, setAppliedSearch] = useState('');
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(20);
	const [rows, setRows] = useState<AdminTimesheet[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [canRestore, setCanRestore] = useState(false);
	const [groups, setGroups] = useState<TimesheetGroupOption[]>([]);
	const [members, setMembers] = useState<TimesheetMemberOption[]>([]);
	const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
	const [restoreVisible, setRestoreVisible] = useState(false);
	const [restoreReason, setRestoreReason] = useState('');
	const [restoreRequestId, setRestoreRequestId] = useState(createRequestId());
	const [restoring, setRestoring] = useState(false);
	const [detailVisible, setDetailVisible] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detail, setDetail] = useState<any>(null);

	const loadTimesheets = useCallback(async () => {
		setLoading(true);
		try {
			const response = await getAdminTimesheets({
				status,
				startDate: dateRange[0].format('YYYY-MM-DD'),
				endDate: dateRange[1].format('YYYY-MM-DD'),
				page,
				size,
				groupId,
				groupUserId,
				search: appliedSearch,
			});
			const data = response.data?.data;
			setRows(data?.docs || []);
			setTotal(data?.total || 0);
			setCanRestore(Boolean(data?.permissions?.canRestore));
			setSelectedIds([]);
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Unable to load timesheets.');
		} finally {
			setLoading(false);
		}
	}, [appliedSearch, dateRange, groupId, groupUserId, page, size, status]);

	useEffect(() => {
		loadTimesheets();
	}, [loadTimesheets]);

	const loadGroups = async (value = '') => {
		try {
			const response = await getAdminTimesheetGroups(value);
			setGroups(response.data?.data || []);
		} catch (_) {
			message.error('Unable to load groups.');
		}
	};

	useEffect(() => {
		loadGroups();
	}, []);

	useEffect(() => {
		setGroupUserId(undefined);
		setMembers([]);
		if (!groupId) return;
		getAdminTimesheetMembers(groupId)
			.then(response => setMembers(response.data?.data || []))
			.catch(() => message.error('Unable to load group members.'));
	}, [groupId]);

	const openDetail = async (row: AdminTimesheet) => {
		setDetailVisible(true);
		setDetailLoading(true);
		try {
			const response = await getAdminTimesheetDetail(row.id || row._id);
			setDetail(response.data?.data || null);
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Unable to load timesheet details.');
		} finally {
			setDetailLoading(false);
		}
	};

	const openRestore = () => {
		setRestoreReason('');
		setRestoreRequestId(createRequestId());
		setRestoreVisible(true);
	};

	const restoreSelected = async () => {
		const reason = restoreReason.trim();
		if (!reason) {
			message.warning('Please enter a restore reason.');
			return;
		}
		setRestoring(true);
		try {
			const response = await restoreAdminTimesheets({
				timesheetIds: selectedIds.map(String),
				reason,
				requestId: restoreRequestId,
			});
			const result = response.data?.data;
			message.success(response.data?.message || 'Timesheets restored successfully.');
			setRestoreVisible(false);
			await loadTimesheets();
			if (result?.failed?.length) {
				Modal.warning({
					title: `${result.failed.length} timesheet(s) could not be restored`,
					content: (
						<div className="max-h-64 overflow-y-auto">
							{result.failed.map((item: any) => (
								<div key={item.timesheetId} className="mb-2">
									<strong>{item.timesheetId}</strong>: {item.reason}
								</div>
							))}
						</div>
					),
				});
			}
		} catch (error: any) {
			message.error(error?.response?.data?.message || 'Unable to restore timesheets.');
		} finally {
			setRestoring(false);
		}
	};

	const columns: ColumnsType<AdminTimesheet> = [
		{ title: 'GROUP', key: 'group', render: (_, row) => row.group?.name || row.groupId },
		{
			title: 'EMPLOYEE',
			key: 'employee',
			render: (_, row) => (
				<div>
					<div>{memberName(row.member)}</div>
					<small className="text-gray-500">{row.member?.email || row.member?.memberCode || row.groupUserId}</small>
				</div>
			),
		},
		{ title: 'DATE', dataIndex: 'recordDate', key: 'recordDate', render: value => moment(value).format('DD/MM/YYYY') },
		{ title: 'SHIFT', key: 'shift', render: (_, row) => row.shiftDetail?.name || row.shiftId || '-' },
		{ title: 'IN', dataIndex: 'timeIn', key: 'timeIn', render: formatMinute },
		{ title: 'OUT', dataIndex: 'timeOut', key: 'timeOut', render: formatMinute },
		{ title: 'TOTAL', dataIndex: 'totalTime', key: 'totalTime', render: value => `${value || 0} min` },
		{
			title: 'STATUS',
			dataIndex: 'isDeleted',
			key: 'isDeleted',
			render: value => (value ? <Tag color="red">DELETED</Tag> : <Tag color="green">ACTIVE</Tag>),
		},
		{
			title: 'RESTORE CHECK',
			key: 'restoreCheck',
			render: (_, row) =>
				!row.isDeleted ? (
					'-'
				) : row.restoreBlockedReason ? (
					<span className="text-red-600">{row.restoreBlockedReason}</span>
				) : (
					<Tag color="green">READY</Tag>
				),
		},
		{
			title: 'UPDATED',
			dataIndex: 'updated',
			key: 'updated',
			render: value => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'),
		},
		{
			title: 'ACTION',
			key: 'action',
			render: (_, row) => <Button onClick={() => openDetail(row)}>View</Button>,
		},
	];

	const imageUrl = (timesheetId: string, type: 'in' | 'out') =>
		`${configApp.hostApi}/${configApp.apiPrefix}/group/get-image-timesheet/${encodeURIComponent(timesheetId)}?type=${type}`;
	const selectedTimesheet = detail?.timesheet as AdminTimesheet | undefined;

	return (
		<div>
			<h1 className="mb-4 text-base font-bold">TIMESHEET MANAGEMENT</h1>
			<div className="mb-4 rounded-lg bg-white p-4 shadow">
				<Tabs
					activeKey={status}
					onChange={key => {
						setStatus(key as TimesheetStatusFilter);
						setPage(1);
					}}
					items={[
						{ key: 'deleted', label: 'Deleted' },
						{ key: 'active', label: 'Active' },
						{ key: 'all', label: 'All' },
					]}
				/>
				<div className="flex flex-wrap gap-3">
					<RangePicker
						value={dateRange}
						format="DD/MM/YYYY"
						onChange={value => {
							if (value?.[0] && value?.[1]) setDateRange([value[0], value[1]]);
							setPage(1);
						}}
					/>
					<Select
						allowClear
						showSearch
						filterOption={false}
						placeholder="Select group"
						style={{ minWidth: 220 }}
						value={groupId}
						onSearch={loadGroups}
						onChange={value => {
							setGroupId(value);
							setPage(1);
						}}
						options={groups.map(group => ({ value: group.id, label: `${group.name}${group.code ? ` (${group.code})` : ''}` }))}
					/>
					<Select
						allowClear
						showSearch
						optionFilterProp="label"
						disabled={!groupId}
						placeholder="Select employee"
						style={{ minWidth: 240 }}
						value={groupUserId}
						onChange={value => {
							setGroupUserId(value);
							setPage(1);
						}}
						options={members.map(member => ({
							value: member.id,
							label: `${[member.fname, member.lname].filter(Boolean).join(' ')}${member.email ? ` (${member.email})` : ''}`,
						}))}
					/>
					<Input.Search
						allowClear
						placeholder="Timesheet ID, employee name or email"
						style={{ width: 300 }}
						value={search}
						onChange={event => setSearch(event.target.value)}
						onSearch={value => {
							setAppliedSearch(value.trim());
							setPage(1);
						}}
					/>
				</div>
			</div>

			{status === 'deleted' && canRestore && (
				<div className="mb-3">
					<Button type="primary" disabled={!selectedIds.length} onClick={openRestore}>
						Restore selected ({selectedIds.length})
					</Button>
				</div>
			)}
			<div className="rounded-lg bg-white p-3 shadow">
				<Table
					rowKey={row => row.id || row._id}
					loading={loading}
					columns={columns}
					dataSource={rows}
					pagination={false}
					scroll={{ x: 1250 }}
					rowSelection={
						status === 'deleted' && canRestore
							? {
								selectedRowKeys: selectedIds,
								onChange: setSelectedIds,
								getCheckboxProps: row => ({
									disabled: Boolean(row.restoreBlockedReason),
									title: row.restoreBlockedReason || undefined,
								}),
							}
							: undefined
					}
				/>
				<div className="mt-3 flex justify-end">
					<Pagination
						current={page}
						pageSize={size}
						total={total}
						showSizeChanger
						pageSizeOptions={['20', '50', '100']}
						onChange={(nextPage, nextSize) => {
							setPage(nextSize !== size ? 1 : nextPage);
							setSize(nextSize);
						}}
					/>
				</div>
			</div>

			<Modal
				title={`Restore ${selectedIds.length} timesheet(s)`}
				visible={restoreVisible}
				onCancel={() => setRestoreVisible(false)}
				onOk={restoreSelected}
				confirmLoading={restoring}
				okText="Restore"
				okButtonProps={{ danger: true }}
			>
				<Alert
					type="warning"
					showIcon
					className="mb-3"
					message="Timesheets in closed salary periods or conflicting with active records will not be restored."
				/>
				<Input.TextArea
					rows={4}
					maxLength={500}
					showCount
					value={restoreReason}
					onChange={event => setRestoreReason(event.target.value)}
					placeholder="Required: explain why these timesheets are being restored"
				/>
			</Modal>

			<Drawer
				title="Timesheet details"
				width={720}
				visible={detailVisible}
				onClose={() => setDetailVisible(false)}
			>
				<Spin spinning={detailLoading}>
					{selectedTimesheet && (
						<>
							<Descriptions bordered column={2} size="small">
								<Descriptions.Item label="Timesheet ID" span={2}>{selectedTimesheet.id}</Descriptions.Item>
								<Descriptions.Item label="Group">{selectedTimesheet.group?.name || selectedTimesheet.groupId}</Descriptions.Item>
								<Descriptions.Item label="Employee">{memberName(selectedTimesheet.member)}</Descriptions.Item>
								<Descriptions.Item label="Date">{moment(selectedTimesheet.recordDate).format('DD/MM/YYYY')}</Descriptions.Item>
								<Descriptions.Item label="Shift">{selectedTimesheet.shiftDetail?.name || selectedTimesheet.shiftId || '-'}</Descriptions.Item>
								<Descriptions.Item label="Time in">{formatMinute(selectedTimesheet.timeIn)}</Descriptions.Item>
								<Descriptions.Item label="Time out">{formatMinute(selectedTimesheet.timeOut)}</Descriptions.Item>
								<Descriptions.Item label="Break">{selectedTimesheet.breakTime || 0} min</Descriptions.Item>
								<Descriptions.Item label="Total">{selectedTimesheet.totalTime || 0} min</Descriptions.Item>
								<Descriptions.Item label="Type">{selectedTimesheet.type || '-'}</Descriptions.Item>
								<Descriptions.Item label="Status">{selectedTimesheet.isDeleted ? 'Deleted' : 'Active'}</Descriptions.Item>
								<Descriptions.Item label="Note" span={2}>{selectedTimesheet.note || '-'}</Descriptions.Item>
								<Descriptions.Item label="Check-in location" span={2}>{selectedTimesheet.checkinAddress || `${selectedTimesheet.checkinLat || '-'}, ${selectedTimesheet.checkinLng || '-'}`}</Descriptions.Item>
								<Descriptions.Item label="Check-out location" span={2}>{selectedTimesheet.checkoutAddress || `${selectedTimesheet.checkoutLat || '-'}, ${selectedTimesheet.checkoutLng || '-'}`}</Descriptions.Item>
							</Descriptions>
							<Space className="mt-4" align="start">
								{selectedTimesheet.checkinImage && <Image width={220} src={imageUrl(selectedTimesheet.id, 'in')} alt="Check-in" />}
								{selectedTimesheet.checkoutImage && <Image width={220} src={imageUrl(selectedTimesheet.id, 'out')} alt="Check-out" />}
							</Space>
							<div className="mt-5 font-semibold">In/out history ({detail?.histories?.length || 0})</div>
							<Table
								className="mt-2"
								size="small"
								pagination={false}
								rowKey={(row: any) => row.id || row._id}
								dataSource={detail?.histories || []}
								columns={[
									{ title: 'In', dataIndex: 'timeIn', render: formatMinute },
									{ title: 'Out', dataIndex: 'timeOut', render: formatMinute },
									{ title: 'Total', dataIndex: 'totalTime', render: (value: number) => `${value || 0} min` },
									{ title: 'Status', dataIndex: 'isDeleted', render: (value: boolean) => (value ? 'Deleted' : 'Active') },
								]}
							/>
						</>
					)}
				</Spin>
			</Drawer>
		</div>
	);
}
